import React from "react";
import { Button, StackItem, Text, VStack } from "@astryxdesign/core";
import { useIsClamped } from "../../hooks/useIsClamped";
import styles from "./ExpandableText.module.css";

export interface ExpandableTextProps {
  /** The body copy to render. */
  children: string;
  /**
   * Lines shown before truncating.
   * @default 2
   */
  lines?: number;
  /**
   * Astryx text color. Pass "secondary" for de-emphasised copy such as an
   * empty-state placeholder.
   * @default 'primary'
   */
  color?: "primary" | "secondary";
  /** @default 'See more' */
  expandLabel?: string;
  /** @default 'See less' */
  collapseLabel?: string;
}

/**
 * Body copy clamped to a fixed number of lines, with a toggle that expands it
 * in place. The toggle renders only when the text is actually being cut off,
 * so short copy never shows a control that would do nothing.
 *
 * Renders via Astryx's Text/Button/Stack, so this MUST be rendered inside an
 * Astryx <Theme> scope.
 */
const ExpandableText = ({
  children,
  lines = 2,
  color = "primary",
  expandLabel = "See more",
  collapseLabel = "See less",
}: ExpandableTextProps): React.ReactElement => {
  const [textRef, isClamped] = useIsClamped();
  const [isExpanded, setIsExpanded] = React.useState(false);

  // Expanding removes the clamp, which makes the overflow measurement false —
  // so the expanded state must also keep the toggle mounted, or there would be
  // no way back.
  const showToggle = isClamped || isExpanded;

  return (
    <VStack height="100%" vAlign="between" gap={2}>
      <div
        ref={textRef}
        className={isExpanded ? undefined : styles.clamped}
        style={{ "--expandable-text-lines": lines } as React.CSSProperties}
      >
        <Text color={color}>{children}</Text>
      </div>
      {showToggle && (
        <StackItem crossAlignSelf="start">
          <Button
            variant="ghost"
            size="sm"
            label={isExpanded ? collapseLabel : expandLabel}
            onClick={() => setIsExpanded((prev) => !prev)}
          />
        </StackItem>
      )}
    </VStack>
  );
};

export default ExpandableText;
